/**
 * Product detail page (converted from the Webflow detail_product template).
 *
 * Statically generated for every product handle. Composes the interactive
 * detail hero (gallery + lightbox + variant selectors + add-to-cart), the
 * static "About the product" copy, the reviews slider, the related-products
 * grid (reusing ProductCard), and the gold newsletter CTA from the export.
 */
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { FC } from 'react'
import type { Category, Product } from '@/types'

import { Seo } from '@/components/generals/seo'
import { SiteLayout } from '@/components/layout/site-layout'
import { ProductCard } from '@/components/common/product-card'

import {
    NewsletterCta,
    ProductDetail,
    ReviewsSlider
} from '@/components/product'

import { getLayoutData } from '@/lib/layout-data'

import {
    getProductByHandle,
    getProducts,
    getRelatedProducts
} from '@/lib/collections'

interface ProductPageProps {
    product: Product
    related: Product[]
    categories: Category[]
}

const ProductPage: FC<ProductPageProps> = ({ product, related, categories }) => {
    return (
        <SiteLayout categories={categories}>
            <Seo title={product.name} description={product.description} ogImage={product.mainImage} />

            <ProductDetail key={product.handle} product={product} />

            <div className='section'>
                <div className='container _6ems'>
                    <div className='fade-in-move-on-scroll'>
                        <h4>About the product</h4>
                    </div>
                    <div className='top-margin-l'>
                        <div className='w-layout-grid grid-6-col'>
                            <div id='w-node-_5a1790fe-3e85-72d3-9345-2fe2c09a4756-a35bd841' className='fade-in-move-on-scroll'>
                                <p className='paragraph-m grey-text---light'>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Blandit maecenas volutpat semper suspendisse elit ut. Urna aliquet eget nunc condimentum platea vitae vel lacinia condimentum. Sed proin sed augue fermentum, pharetra turpis. Felis feugiat viverra nulla semper ac tempus condimentum. Neque faucibus tempor, arcu id vulputate consectetur. Eget pellentesque cras euismod et.</p>
                            </div>
                            <div id='w-node-_5e65b295-c032-c819-7f8d-c1642858b4df-a35bd841' className='fade-in-move-on-scroll'>
                                <p className='paragraph-m grey-text---light'>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Blandit maecenas volutpat semper suspendisse elit ut. Urna aliquet eget nunc condimentum platea vitae vel lacinia condimentum. Sed proin sed augue fermentum, pharetra turpis. Felis feugiat viverra nulla semper ac tempus condimentum. Neque faucibus tempor, arcu id vulputate consectetur. Eget pellentesque cras euismod et.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='section overflow-hidden'>
                <div className='divider' />
                <div className='container _6ems'>
                    <div className='slider-holder'>
                        <div className='fade-in-move-on-scroll'>
                            <div className='slider-header-holder'>
                                <h4 className='no-margins'>Reviews</h4>
                                <div className='review-counter-container'>
                                    <h4 className='no-margins'>4.8</h4>
                                    <img src='/images/Star-Orange.svg' loading='lazy' alt='' className='rating-star' />
                                </div>
                            </div>

                            <ReviewsSlider />
                        </div>
                    </div>
                </div>
            </div>

            <div className='section grey-section'>
                <div className='container _6ems'>
                    <div className='center-text'>
                        <h3>People also bought with this</h3>
                    </div>
                    <div className='product-card-holder'>
                        <div className='product-cards-wrapper w-dyn-list'>
                            <div role='list' className='product-grid w-dyn-items'>
                                {related.map((item) => (
                                    <ProductCard key={item.id} product={item} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='section gold-section'>
                <div className='container'>
                    <div className='golden-cta-container'>
                        <div className='w-layout-grid grid-6-col-cta'>
                            <div id='w-node-_6bb5aeaf-6b14-3c04-4aac-2b4453187d3c-53187d38' className='fade-in-move-on-scroll'>
                                <h3 className='white-text no-margins'>Put your hands first at our premium and limited products</h3>
                            </div>
                            <div id='w-node-_6bb5aeaf-6b14-3c04-4aac-2b4453187d3f-53187d38' className='center-text'>
                                <div className='newsletter-email-section'>
                                    <div className='form-block w-form'>
                                        <NewsletterCta />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </SiteLayout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const paths = getProducts().map((product) => ({
        params: { handle: product.handle }
    }))

    return { paths, fallback: false }
}

export const getStaticProps: GetStaticProps<ProductPageProps> = async (context) => {
    const handle = context.params?.handle as string
    const product = getProductByHandle(handle)

    if (!product) {
        return { notFound: true }
    }

    const { categories } = getLayoutData()

    return {
        props: {
            product,
            related: getRelatedProducts(handle),
            categories
        }
    }
}

export default ProductPage
